import { useEffect, useState } from 'react';
import type { OrderBook as OrderBookType, OrderBookEntry } from '../types/trade';
import { api } from '../services/api';

interface OrderBookProps {
  symbol?: string;
  depth?: number;
  refreshInterval?: number;
}

export function OrderBook({
  symbol = 'fco_usdt',
  depth = 15,
  refreshInterval = 2000,
}: OrderBookProps) {
  const [orderBook, setOrderBook] = useState<OrderBookType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrderBook();
    const interval = setInterval(loadOrderBook, refreshInterval);
    return () => clearInterval(interval);
  }, [symbol, refreshInterval]);

  const loadOrderBook = async () => {
    try {
      const raw = await api.getOrderBook(symbol);

      // Compute total (price * quantity) for each entry if not present
      const bids: OrderBookEntry[] = (raw.bids || []).map((b: any) => ({
        price: b.price,
        quantity: b.quantity,
        total: b.total ?? b.price * b.quantity,
      }));
      const asks: OrderBookEntry[] = (raw.asks || []).map((a: any) => ({
        price: a.price,
        quantity: a.quantity,
        total: a.total ?? a.price * a.quantity,
      }));

      // Compute spread if not provided
      const bestBid = bids[0]?.price || 0;
      const bestAsk = asks[0]?.price || 0;
      const mid = (bestBid + bestAsk) / 2;
      const spreadPct = raw.spreadPct ?? (mid > 0 ? ((bestAsk - bestBid) / mid) * 100 : 0);

      setOrderBook({
        bids,
        asks,
        spread: raw.spread ?? (bestAsk - bestBid),
        spreadPct,
        timestamp: raw.timestamp || new Date().toISOString(),
      });
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order book');
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => price.toFixed(5);
  const formatQuantity = (qty: number) => qty.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const formatTotal = (total: number) => `$${total.toFixed(2)}`;

  const getMaxTotal = () => {
    if (!orderBook) return 1;
    const bidMax = Math.max(...orderBook.bids.slice(0, depth).map((b) => b.total), 0);
    const askMax = Math.max(...orderBook.asks.slice(0, depth).map((a) => a.total), 0);
    return Math.max(bidMax, askMax) || 1;
  };

  const renderRow = (entry: OrderBookEntry, side: 'bid' | 'ask', maxTotal: number) => {
    const widthPct = (entry.total / maxTotal) * 100;
    const bgColor = side === 'bid' ? 'rgba(38, 166, 154, 0.2)' : 'rgba(239, 83, 80, 0.2)';

    return (
      <div key={`${side}-${entry.price}`} className={`orderbook-row ${side}`}>
        <div
          className="depth-bar"
          style={{
            width: `${widthPct}%`,
            backgroundColor: bgColor,
            [side === 'bid' ? 'right' : 'left']: 0,
          }}
        />
        <span className="price">{formatPrice(entry.price)}</span>
        <span className="quantity">{formatQuantity(entry.quantity)}</span>
        <span className="total">{formatTotal(entry.total)}</span>
      </div>
    );
  };

  if (loading && !orderBook) {
    return <div className="orderbook loading">Loading order book...</div>;
  }

  if (error) {
    return <div className="orderbook error">{error}</div>;
  }

  if (!orderBook) {
    return <div className="orderbook">No data</div>;
  }

  const maxTotal = getMaxTotal();

  return (
    <div className="orderbook">
      <div className="orderbook-header">
        <h3>Order Book</h3>
        <div className="spread-info">
          <span className={`spread ${orderBook.spreadPct <= 1 ? 'compliant' : 'warning'}`}>
            Spread: {orderBook.spreadPct.toFixed(2)}%
          </span>
        </div>
      </div>

      <div className="orderbook-columns">
        <span>Price</span>
        <span>Qty (FCO)</span>
        <span>Total (USD)</span>
      </div>

      <div className="asks">
        {orderBook.asks.slice(0, depth).reverse().map((ask) => renderRow(ask, 'ask', maxTotal))}
      </div>

      <div className="mid-price">
        <span className="current-price">
          ${((orderBook.bids[0]?.price + orderBook.asks[0]?.price) / 2 || 0).toFixed(5)}
        </span>
      </div>

      <div className="bids">
        {orderBook.bids.slice(0, depth).map((bid) => renderRow(bid, 'bid', maxTotal))}
      </div>

      <div className="orderbook-footer">
        <div className="depth-summary">
          <span className="bid-depth">
            Bid Depth: ${orderBook.bids.slice(0, depth).reduce((sum, b) => sum + b.total, 0).toFixed(2)}
          </span>
          <span className="ask-depth">
            Ask Depth: ${orderBook.asks.slice(0, depth).reduce((sum, a) => sum + a.total, 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}
